import React, { useState, useEffect, useMemo } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_ANALYTICS_STATS, GET_EMPLOYEES } from "./graphql";
import TopServicesPanel from "./TopServicesPanel";

const ServicesDashboard = () => {
  const [level, setLevel] = useState("category");
  const [parentId, setParentId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    employeeIds: [],
    status: "",
  });

  const { data: employeesData } = useQuery(GET_EMPLOYEES, {
    variables: {
      data: {
        bookableOrEmployed: true,
      },
    },
  });

  const [fetchAnalytics, { data, loading }] =
    useLazyQuery(GET_ANALYTICS_STATS);

    useEffect(() => {
      if (employeesData?.getEmployeelist?.employee) {
        setEmployeesList(employeesData.getEmployeelist.employee);
      }
    }, [employeesData]);

  useEffect(() => {
    
    if (!filters.startDate || !filters.endDate) return;

    fetchAnalytics({
      variables: {
        data: {
          ...filters,
          groupBy: level,
          parentId,
        },
      },
      fetchPolicy: "network-only",
    });
  }, [level, parentId, filters]);

  const rows = data?.getServiceStats?.data || [];

  const totals = useMemo(() => {
    return {
      count: rows.reduce(
        (sum, r) => sum + (r.totalCount || 0),
        0
      ),
      revenue: rows.reduce(
        (sum, r) => sum + (r.revenue || 0),
        0
      ),
    };
  }, [rows]);

  const handleRowClick = (row) => {
    if (level === "category") {
      setBreadcrumb((prev) => [
        ...prev,
        { id: row.id, name: row.name, level: "product" },
      ]);
      setLevel("product");
      setParentId(row.id);
    } else if (level === "product") {
      setBreadcrumb((prev) => [
        ...prev,
        { id: row.id, name: row.name, level: "service" },
      ]);
      setLevel("service");
      setParentId(row.id);
    }
  };

  const goHome = () => {
    setLevel("category");
    setParentId(null);
    setBreadcrumb([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8 space-y-8">

      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800">
          Service Analytics
        </h1>
        <p className="text-gray-500 text-sm">
          Drill down Category → Subcategory → Services
        </p>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow">
          <p className="text-sm text-gray-500">Total Count</p>
          <p className="text-2xl font-bold text-indigo-600">
            {totals.count}
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-2xl font-bold text-green-600">
            ₹ {totals.revenue}
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          <p className="text-sm text-gray-500">Current Level</p>
          <p className="text-2xl font-semibold capitalize">
            {level}
          </p>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white p-6 rounded-xl shadow grid grid-cols-4 gap-6">

        <div>
          <label className="text-xs text-gray-500">
            Start Date
          </label>
          <div className="relative">
          <input
            type="date"
            className="w-full border rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-400 outline-none"
            value={filters.startDate}
            onChange={(e) =>
              setFilters({
                ...filters,
                startDate: e.target.value,
              })
            }
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-black pointer-events-none">
    📅
  </span>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500">
            End Date
          </label>
          <div className="relative">
  <input
            type="date"
           className="w-full border rounded-lg px-3 py-2 mt-1 bg-white text-gray-800 focus:ring-2 focus:ring-indigo-400 outline-none"
            value={filters.endDate}
            onChange={(e) =>
              setFilters({
                ...filters,
                endDate: e.target.value,
              })
            }
          />
  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-black pointer-events-none">
    📅
  </span>
</div>
          
        </div>

        <div>
          <label className="text-xs text-gray-500">
            Employee
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-400 outline-none"
            value={""}
            onChange={(e) =>
              setFilters({
                ...filters,
                employeeIds: e.target.value
                  ? [e.target.value]
                  : [],
              })
            }
          >
            <option value="">All Employees</option>
            {employeesList.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.fullName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-gray-500">
            Status
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-400 outline-none"
            value={filters.status}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: e.target.value,
              })
            }
          >
            <option value="">All</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
      </div>

      {/* BREADCRUMB */}
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <span
          onClick={goHome}
          className="cursor-pointer hover:text-indigo-600 font-medium"
        >
          Categories
        </span>

        {breadcrumb.map((item, index) => (
          <span key={item.id} className="flex gap-2">
            <span>›</span>
            <span
              className="cursor-pointer hover:text-indigo-600"
              onClick={() => {
                setLevel(item.level);
                setParentId(item.id);
                setBreadcrumb(
                  breadcrumb.slice(0, index + 1)
                );
              }}
            >
              {item.name}
            </span>
          </span>
        ))}
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-gray-500">
            Loading...
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="text-left px-6 py-3">
                  Name
                </th>
                <th className="text-right px-6 py-3">
                  Count
                </th>
                <th className="text-right px-6 py-3">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() =>
                    level !== "service" &&
                    handleRowClick(row)
                  }
                  className={`border-t transition ${
                    level !== "service"
                      ? "hover:bg-indigo-50 cursor-pointer"
                      : ""
                  }`}
                >
                  <td className="px-6 py-4 text-gray-800">
                    {row.name}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-indigo-600">
                    {row.totalCount}
                  </td>
                  <td className="px-6 py-4 text-right text-green-600">
                    ₹ {row.revenue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <TopServicesPanel filters={filters} />

    </div>
  );
};

export default ServicesDashboard;